// src/utils/summary/helpers.ts

interface Summary {
  version: number;
  [key: string]: any;
}

/**
 * Finds the summary with the highest version number from an array of summaries.
 * @param {Summary[]} summaries - An array of summary objects, each with a 'version' property.
 * @returns {Summary|null} The summary object with the highest version, or null if the input is empty.
 */
export const getLatestSummary = (summaries: Summary[]): Summary | null => {
  if (!summaries || summaries.length === 0) {
    return null;
  }
  return summaries.reduce((latest: Summary | null, current: Summary) => {
    if (!latest) {
      return current;
    }
    return current.version > latest.version ? current : latest;
  }, null);
};

interface PrescriptionFormValues {
  order_type_id?: string;
  name: string;
  dosage: string;
  dosage_unit: string;
  frequency: string;
  duration: string;
  condition: string;
  associated_diagnosis_code?: string;
  snomed_code?: string;
  snomed_code_description?: string;
  warnings?: string | string[];
}

interface ExistingPrescription {
  confidence?: number;
  order_type_id?: string;
}

export const createMedicinePayload = (
  values: PrescriptionFormValues,
  existingPrescription: ExistingPrescription | null = null
) => {
  const confidence = existingPrescription?.confidence
    ? existingPrescription.confidence / 100
    : 1.0;

  return {
    order_type_id:
      existingPrescription?.order_type_id || values.order_type_id || null,
    medication_name: values.name,
    dosage: values.dosage || 'As prescribed',
    dosage_unit: values.dosage_unit,
    frequency: values.frequency || 'As directed',
    duration: values.duration || 'As directed',
    evidence: values.condition,
    confidence: confidence,
    icd10_code: values.associated_diagnosis_code || '',
    snomed_code: values.snomed_code || '',
    snomed_code_description: values.snomed_code_description || '',
    safety_alerts: values.warnings,
  };
};
